import { useMemo } from 'react'
import {
  isLearnerAccount,
  isTeacherAccount,
  isParentAccount,
  isAdminAccount,
  type AccountsState,
} from '../../../../accounts'
import { AppState } from '../../../../app/types'
import { APP_NAME } from '../../../../config/constants'
import { formatRelativeTime } from '../../../../utils/formatters'
import { getModuleLabel, resolveModuleCode } from '../../shared/moduleMetadata'
import type {
  UserRow,
  SchoolData,
  RoleFilter,
  FamilyMasteryEntry,
} from './types'

interface UseUsersDataParams {
  accounts: AccountsState | undefined
  getLearnerData: ((learnerId: string) => AppState | null) | undefined
  roleFilter: RoleFilter
  searchQuery: string
  schoolFilter: string
  selectedUser: UserRow | null
}

export function useUsersData({
  accounts,
  getLearnerData,
  roleFilter,
  searchQuery,
  schoolFilter,
  selectedUser,
}: UseUsersDataParams) {
  // Build enriched user list
  const users = useMemo((): UserRow[] => {
    if (!accounts || !accounts.accountsById) return []
    const allAccounts = Object.values(accounts.accountsById)
    return allAccounts.map((account): UserRow => {
      let organization = '—'
      let linkedCount = 0
      let xp = 0
      let avgMastery = 0
      let sessionCount = 0
      let familyMastery: Record<string, FamilyMasteryEntry> = {}
      let groupName: string | undefined
      if (isTeacherAccount(account)) {
        organization = account.schoolName || '—'
        linkedCount = (
          accounts.relationships.teacherToLearners[account.id] || []
        ).length
        // Derive classroom name from top-level registry
        const teacherClassrooms = Object.values(
          accounts.classrooms ?? {},
        ).filter((c) => c.teacherId === account.id)
        if (teacherClassrooms.length > 0) {
          groupName = teacherClassrooms.map((c) => c.name).join(', ')
        }
      } else if (isParentAccount(account)) {
        linkedCount = (
          accounts.relationships.parentToLearners[account.id] || []
        ).length
      } else if (isLearnerAccount(account)) {
        const teacherId = Object.entries(
          accounts.relationships.teacherToLearners || {},
        ).find(([_, ids]) => ids.includes(account.id))?.[0]
        const teacher = teacherId ? accounts.accountsById[teacherId] : null
        organization =
          teacher && isTeacherAccount(teacher) ? teacher.schoolName || '—' : '—'
        const parentId = Object.entries(
          accounts.relationships.parentToLearners || {},
        ).find(([_, ids]) => ids.includes(account.id))?.[0]
        linkedCount = parentId ? 1 : 0
        if (getLearnerData) {
          const data = getLearnerData(account.id)
          xp = data?.stats?.xp ?? 0
          const rawMastery = data?.familyMastery || {}
          familyMastery = rawMastery as Record<string, FamilyMasteryEntry>
          const masteryEntries = Object.values(familyMastery)
          const masteryValues = masteryEntries.map((m) => m.masteryScore)
          avgMastery =
            masteryValues.length > 0
              ? Math.round(
                  masteryValues.reduce((sum, val) => sum + val, 0) /
                    masteryValues.length,
                )
              : 0
          const totalAttempts = masteryEntries.reduce(
            (sum, m) => sum + m.attempts,
            0,
          )
          sessionCount = Math.ceil(totalAttempts / 10)
        }
        groupName = account.grade ? `Groep ${account.grade}` : undefined
      } else if (isAdminAccount(account)) {
        organization = APP_NAME
      }
      return {
        id: account.id,
        name: account.displayName,
        email: account.email || '—',
        role: account.role,
        organization,
        linkedCount,
        xp,
        lastActive: account.lastActive || 0,
        lastActiveFormatted: formatRelativeTime(account.lastActive, true),
        status: 'active',
        account,
        avgMastery,
        sessionCount,
        groupName,
        familyMastery,
      }
    })
  }, [accounts, getLearnerData])

  // Build schools data for school view
  const schoolsData = useMemo((): SchoolData[] => {
    const schoolNames = Array.from(
      new Set(users.map((u) => u.organization).filter((o) => o !== '—')),
    ).sort()
    return schoolNames.map((schoolName) => {
      const schoolTeachers = users.filter(
        (u) => u.role === 'teacher' && u.organization === schoolName,
      )
      const schoolLearners = users.filter(
        (u) => u.role === 'learner' && u.organization === schoolName,
      )
      const avgMastery =
        schoolLearners.length > 0
          ? Math.round(
              schoolLearners.reduce((sum, l) => sum + (l.avgMastery || 0), 0) /
                schoolLearners.length,
            )
          : 0
      return {
        name: schoolName,
        learnerCount: schoolLearners.length,
        teacherCount: schoolTeachers.length,
        avgMastery,
        learners: schoolLearners,
        teachers: schoolTeachers,
      }
    })
  }, [users])

  // Get unique schools for filter dropdown
  const allSchools = useMemo(() => {
    return Array.from(
      new Set(users.map((u) => u.organization).filter((o) => o !== '—')),
    ).sort()
  }, [users])

  // Counts
  const counts = useMemo(
    () => ({
      all: users.length,
      learner: users.filter((u) => u.role === 'learner').length,
      teacher: users.filter((u) => u.role === 'teacher').length,
      parent: users.filter((u) => u.role === 'parent').length,
      admin: users.filter((u) => u.role === 'admin').length,
      school: schoolsData.length,
    }),
    [users, schoolsData],
  )

  // Filter users based on role and search
  const filtered = useMemo(() => {
    let result = users
    if (roleFilter !== 'all' && roleFilter !== 'school') {
      result = result.filter((u) => u.role === roleFilter)
    }
    if (schoolFilter && roleFilter === 'learner') {
      result = result.filter((u) => u.organization === schoolFilter)
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.organization.toLowerCase().includes(q),
      )
    }
    return result
  }, [users, roleFilter, searchQuery, schoolFilter])

  // Filter schools for school view
  const filteredSchools = useMemo(() => {
    if (!searchQuery) return schoolsData
    const q = searchQuery.toLowerCase()
    return schoolsData.filter((s) => s.name.toLowerCase().includes(q))
  }, [schoolsData, searchQuery])

  // Mastery by subject for selected learner
  const selectedMastery = useMemo(() => {
    if (!selectedUser || selectedUser.role !== 'learner') return []
    const fm = selectedUser.familyMastery || {}
    const bySubject: Record<string, number[]> = {}
    Object.values(fm).forEach((m) => {
      const rawCode = m.familyId?.split('-')[0] ?? 'UNK'
      const subjectId = resolveModuleCode(rawCode)
      if (!bySubject[subjectId]) bySubject[subjectId] = []
      bySubject[subjectId].push(m.masteryScore)
    })
    return Object.entries(bySubject)
      .map(([subjectId, values]) => ({
        subjectId,
        label: getModuleLabel(subjectId),
        avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
        count: values.length,
      }))
      .sort((a, b) => b.avg - a.avg)
  }, [selectedUser])

  return {
    users,
    schoolsData,
    allSchools,
    counts,
    filtered,
    filteredSchools,
    selectedMastery,
  }
}
